export const OPERATORS_PROJECT_NAME = "Operators";
export const IS_OPERATORS_APP = true;

export type OperatorsRole = "admin" | "member";

export type OperatorsUser = {
  authProvider: string;
  email: string;
  name: string;
  role: OperatorsRole;
  subject: string;
};

export type OperatorsSessionParticipant = {
  email: string;
  firstSeenAt: string;
  lastSeenAt: string;
  name: string;
  participation: "driver" | "viewer";
  role: OperatorsRole;
};

export type OperatorsSession = {
  archivedAt: string | null;
  createdAt: string;
  id: string;
  interactionMode: "default" | "plan";
  lastInteractionAt: string | null;
  model: string;
  owner: {
    email: string;
    name: string;
    role: OperatorsRole;
  };
  participants: OperatorsSessionParticipant[];
  providerName: "codex";
  providerThreadId: string | null;
  runtimeMode: "approval-required" | "full-access";
  snapshotVersion: string | null;
  status: "archived" | "running" | "stopped";
  title: string;
  updatedAt: string;
  vm: {
    id: string;
    status: "running" | "stopped";
  } | null;
};

export function isOperatorsSessionParticipant(
  session: Pick<OperatorsSession, "owner" | "participants">,
  email: string | null,
): boolean {
  return Boolean(
    email &&
    (session.owner.email === email ||
      session.participants.some((participant) => participant.email === email)),
  );
}

export type OperatorsSessionsResponse = {
  items: OperatorsSession[];
  nextCursor: string | null;
};

export type OperatorsAutomationSchedule =
  | { every: number; type: "interval"; unit: "minute" | "hour" | "day" | "week" }
  | {
      time: string;
      timeZone: string;
      type: "weekly";
      weekdays: Array<"MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU">;
    };

export type OperatorsAutomationTarget =
  | { sessionId: string; type: "existing_thread" }
  | {
      model: string;
      reasoningEffort: "low" | "medium" | "high" | "xhigh" | "max";
      type: "new_thread";
    };

export type OperatorsAutomation = {
  archivedAt: string | null;
  createdAt: string;
  id: string;
  name: string;
  nextRunAt: string | null;
  ownerUserId: string;
  prompt: string;
  schedule: OperatorsAutomationSchedule;
  status: "active" | "paused" | "archived";
  target: OperatorsAutomationTarget;
  updatedAt: string;
  version: number;
};

export type OperatorsAutomationsResponse = { items: OperatorsAutomation[] };

export type OperatorsSkillStatus = "active" | "disabled" | "archived";

export type OperatorsSkill = {
  builtin: boolean;
  createdAt: string;
  description: string;
  id: string;
  name: string;
  ownerEmail: string | null;
  locked: boolean;
  status: OperatorsSkillStatus;
  updatedAt: string;
  version: number | null;
};

export type OperatorsSessionSkill = {
  availableInSession: boolean;
  description: string;
  name: string;
  status: OperatorsSkillStatus;
  version: number | null;
};

export type OperatorsSessionSkillsResponse = { items: OperatorsSessionSkill[] };

export type OperatorsSkillFile = {
  mode: number;
  path: string;
  sha256: string;
  size: number;
  /** null for binary files, which are export-only. */
  text: string | null;
};

export type OperatorsSkillVersion = {
  createdAt: string;
  createdByEmail: string | null;
  description: string;
  id: string;
  manifestSha256: string;
  name: string;
  version: number;
};

export type OperatorsSkillDetail = OperatorsSkill & {
  canEdit: boolean;
  canManage: boolean;
  files: OperatorsSkillFile[];
  versions: OperatorsSkillVersion[];
};

export type OperatorsSkillsResponse = { items: OperatorsSkill[] };

export type OperatorsAutomationRun = {
  automationId: string;
  completedAt: string | null;
  createdAt: string;
  error: string | null;
  id: string;
  scheduledFor: string;
  sessionId: string | null;
  status: "pending" | "queued" | "running" | "succeeded" | "failed";
  trigger: "scheduled" | "manual";
  updatedAt: string;
};

export type OperatorsSessionSearchMatch = {
  kind: "message" | "title";
  matchedAt: string;
  messageId: string | null;
  role: string | null;
  snippet: string | null;
};

export type OperatorsSessionSearchResult = {
  match: OperatorsSessionSearchMatch;
  session: OperatorsSession;
};

export type OperatorsSessionSearchResponse = {
  items: OperatorsSessionSearchResult[];
};

export type OperatorsUsageMonthlyUserSummary = {
  cachedInputTokens: number;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  threads: number;
  totalTokens: number;
  userEmail: string | null;
  userId: string | null;
  userName: string | null;
};

export type OperatorsUsageMonthlyModelSummary = {
  cachedInputTokens: number;
  inputTokens: number;
  model: string;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
};

export type OperatorsUsageMonthlyResponse = {
  cachedInputTokens: number;
  inputTokens: number;
  month: string;
  modelBreakdown: OperatorsUsageMonthlyModelSummary[];
  outputTokens: number;
  reasoningTokens: number;
  threadCount: number;
  totalTokens: number;
  userBreakdown: OperatorsUsageMonthlyUserSummary[];
};

export type OperatorsAdminUser = {
  createdAt: string;
  displayName: string;
  email: string;
  id: string;
  isSuperAdmin: boolean;
  roles: string[];
  status: "active" | "suspended";
  updatedAt: string;
};

export type OperatorsAdminUsersResponse = {
  items: OperatorsAdminUser[];
  roleKeys: string[];
};

export type OperatorsAdminEffectivePermission = {
  key: string;
  roles: string[];
};

export type OperatorsAdminEffectiveResponse = {
  isSuperAdmin: boolean;
  permissions: OperatorsAdminEffectivePermission[];
  roles: string[];
  status: "active" | "suspended";
  userId: string;
};

export type OperatorsAdminAuditEvent = {
  action: string;
  actorUserId: string | null;
  after: Record<string, unknown> | null;
  before: Record<string, unknown> | null;
  createdAt: string;
  id: string;
  ip: string | null;
  targetId: string;
  targetType: string;
};

export type OperatorsAdminAuditResponse = {
  items: OperatorsAdminAuditEvent[];
  nextCursor: string | null;
};

export type OperatorsAdminToolsResponse = {
  flags: Array<{
    description: string;
    enabled: boolean;
    enabledForRoles: string[];
    enabledForUsers: string[];
    key: string;
  }>;
  tools: Array<{
    flagEnabled: boolean | null;
    flagKey: string | null;
    icon: string;
    name: string;
    navPermission: string | null;
    owner: string;
    route: string;
    slug: string;
    status: string;
  }>;
};

function httpOriginFromWsUrl(wsUrl: string): string {
  const httpUrl = wsUrl.replace(/^wss:/, "https:").replace(/^ws:/, "http:");
  return new URL(httpUrl).origin;
}

export function getOperatorsHttpOrigin(): string {
  const envHttpUrl = import.meta.env.VITE_OPERATORS_HTTP_URL;
  if (typeof envHttpUrl === "string" && envHttpUrl.length > 0) {
    return new URL(envHttpUrl).origin;
  }

  const bridgeWsUrl = window.desktopBridge?.getWsUrl?.();
  if (typeof bridgeWsUrl === "string" && bridgeWsUrl.length > 0) {
    return httpOriginFromWsUrl(bridgeWsUrl);
  }

  const envWsUrl = import.meta.env.VITE_WS_URL;
  if (typeof envWsUrl === "string" && envWsUrl.length > 0) {
    return httpOriginFromWsUrl(envWsUrl);
  }

  return window.location.origin;
}

export function getGoogleDomainHint(): string {
  const domainHint = import.meta.env.VITE_GOOGLE_DOMAIN_HINT;
  if (typeof domainHint !== "string") {
    return "craniometrix.com";
  }

  return domainHint.trim().toLowerCase() || "craniometrix.com";
}

export function sessionStatusSummary(session: OperatorsSession): string {
  if (session.status === "archived") {
    return "Archived";
  }

  if (session.vm?.status === "running") {
    return "VM running";
  }

  if (session.status === "running") {
    return "Active";
  }

  return "Stopped";
}

export function sessionParticipantsSummary(session: OperatorsSession): string {
  const count = session.participants.length;
  if (count === 0) {
    return "No participants yet";
  }

  if (count === 1) {
    return "1 participant";
  }

  return `${count} participants`;
}

export function formatOperatorsSessionMetadata(session: OperatorsSession): string {
  return [
    `Created by ${session.owner.name}`,
    sessionParticipantsSummary(session),
    sessionStatusSummary(session),
  ].join(" · ");
}
